// SaaS-specific config cho @cnv/ui's AppLayout (2-tier sidebar identical Core).
// Schema NAV_GROUPS clone NGUYÊN từ Core's adminMenu — đặc biệt là `chat` group + `system` group.
// SaaS-only items (API Keys, Webhooks) đặt vào `system` group với `group: 'Tích hợp'`.

import { msg } from '@lingui/core/macro';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle, MessagesSquare, Target, BarChart3, Settings, FileCheck2, LifeBuoy, CheckSquare, LayoutDashboard, Users, Briefcase, Zap, CreditCard, Wrench, Building2} from 'lucide-react';
import AppLayoutShared from '@cnv/ui/layout/AppLayout';
import ModuleLockScreen, { TrialExpiredScreen } from '@cnv/ui/pages/marketplace/ModuleLockScreen';
import { buildRouteMap, checkRoutePermission } from '../lib/routePermission';
import { buildModuleRouteMap, lockedModuleForPath } from '../lib/moduleLock';
import { prefetchAllRoutes } from '@cnv/ui/utils/lazyWithReload';
import ApprovalsSecondaryNav from '@cnv/ui/layout/sidebar/ApprovalsSecondaryNav';
import TasksSecondaryNav from '@cnv/ui/layout/sidebar/TasksSecondaryNav';
import useSidebarBadges from '@cnv/ui/hooks/useSidebarBadges';
import useBadgeRealtime from '@cnv/ui/hooks/useBadgeRealtime';
import { useTeamChatUnreadTotal } from '@cnv/ui/lib/teamChatUnread';
import api from '@host/axios';
import { useAuthStore } from '../lib/authStore';
import { useAuth } from '../contexts/AuthContext';
import TopBarV2 from './v2/TopBarV2';
import UiVersionSwitch from '@cnv/ui/layout/UiVersionSwitch';
import RailBottomV2 from './v2/RailBottomV2';
import ProductNewsHost from '@cnv/ui/product-news/ProductNewsHost';
import { UI_V1, UI_V2, applyUiVersion, isTenantAllowed, readUiVersion, subscribeUiVersion } from '../lib/uiVersion';

// Permission gate helper — item.permission='storage:view_all' → user phải có
// permission đó trong currentUser.permissions hoặc là admin (org_role mapped).
// Empty / null permission → ai cũng thấy.
function userHasPermission(user, perm) {
  if (!perm) return true;
  if (user?.role === 'admin') return true;
  const perms = user?.permissions || [];
  if (perms.includes('*')) return true;
  return perms.includes(perm);
}

// ⚠️ KHÔNG ẩn menu theo bản quyền tính năng. Sếp Phú chốt 18/08/2026: người chưa được cấp
// chỗ ngồi Hộp thư **vẫn vào được**, chỉ bị khoá mờ bên trong (xem 20 hội thoại gần nhất,
// không thao tác được gì) để họ sốt ruột mà đi xin quản trị. Ẩn menu là giấu luôn thứ mình
// muốn bán. Lớp khoá mờ nằm ở cnv-ui ChatPage, cờ đọc từ `user.module_seats`.

// Sidebar badge endpoints — pure data signals fetched mỗi 30s (poll) + tab focus + manual trigger.
// Khóa = menu group key trong NAV_GROUPS. Khớp Core's SIDEBAR_BADGE_ENDPOINTS để
// chung 1 nguồn @cnv/ui/hooks/useSidebarBadges (shared kernel).
// `team_chat` KHÔNG dùng polling — qua STOMP store useTeamChatUnreadTotal (realtime).
// TẤT CẢ trỏ 1 endpoint gộp /badges/summary → useSidebarBadges dedup theo url → 1 request/poll
// thay vì 5 (xem modules/badges + useSidebarBadges dedup).
const SIDEBAR_BADGE_ENDPOINTS = {
  tasks:       { url: '/badges/summary', pick: (r) => r.data?.data?.tasks || 0 },
  approvals:   { url: '/badges/summary', pick: (r) => r.data?.data?.approvals || 0 },
  support:     { url: '/badges/summary', pick: (r) => r.data?.data?.support || 0 },
  // Inbox = hội thoại CÓ TIN CHƯA ĐỌC (sếp Phú chốt 08/08). Nhóm và item «Hộp thư» dùng
  // CÙNG một số nên luôn khớp nhau — trước đó nhóm là `inbox_total` (của tôi + cầu cứu = 75)
  // còn item là `inbox_sos` (chỉ cầu cứu = 62), hai nghĩa khác nhau nên nhìn vào không hiểu.
  // `unread_mine` đếm theo TỪNG NGƯỜI (kernel + conversationReads): đồng nghiệp đọc KHÔNG
  // làm tắt badge của mình.
  chat:        { url: '/badges/summary', pick: (r) => r.data?.data?.inbox_unread || 0 },
  'chat-inbox': { url: '/badges/summary', pick: (r) => r.data?.data?.inbox_unread || 0 },
  // Câu hỏi khó chưa ai chấm đúng/sai — bằng ĐÚNG chip "Chờ xử lý" trên trang Trợ giúp AI.
  // KHÔNG gộp vào /badges/summary: query này nặng hơn (CTE + LATERAL trên chat_messages) và
  // chỉ người có quyền AI mới xem, gộp là bắt mọi user trả giá mỗi vòng poll.
  'chat-ai-assist': { url: '/inbox/ai/escalations/pending-count', pick: (r) => Number(r.data?.data?.count) || 0 },
  recruitment: { url: '/badges/summary', pick: (r) => r.data?.data?.recruitment || 0 },
  // File xuất XONG mà mình chưa tải lần nào — thiết kế gốc muốn "chấm đỏ ở lối tắt cạnh chuông",
  // nhưng layout SaaS không có thanh trên cùng nên báo ở badge nhóm Tiện ích (cùng đường badge sẵn có).
  utilities: { url: '/badges/summary', pick: (r) => r.data?.data?.exports || 0 },
};

// Custom secondary nav per group key — match Core SidebarSecondary.jsx CUSTOM_SECONDARY_NAVS:
// - approvals: 12+ filter items (tab tasks/sent)
// - tasks: filter mine/created/assigned/following + project tree + stage filters
const CUSTOM_SECONDARY = {
  approvals: ApprovalsSecondaryNav,
  tasks: TasksSecondaryNav,
};

const NAV_GROUPS = [
  // ===== Dashboard — single-link, clone 1:1 Core adminMenu (key='dashboard') =====
  // 17/08: bỏ submenu vì nó chỉ có ĐÚNG 1 mục trùng tên nhóm — nav phụ 240px mở ra không
  // thêm thông tin gì, chỉ ăn chiều ngang của chính trang tổng quan. Khai như Automation:
  // `path` + KHÔNG `items` (để `items: []` là bộ lọc cuối vứt cả group → mất menu).
  // `permission` ở CẤP NHÓM — bỏ items mà không chuyển gate lên đây là trang tổng quan hở
  // cho mọi người đăng nhập.
  {
    key: 'dashboard',
    module: 'dashboard',
    label: msg`Tổng quan`,
    icon: LayoutDashboard,
    basePath: '/dashboard',
    path: '/dashboard',
    permission: 'dashboard_overview:view',
  },

  // ===== Nhân sự (Employees) — clone 1:1 Core adminMenu (key='employees') — 27 items =====
  // Sub-group via group: Nhân sự / Hợp đồng / Lương / Chấm công / Nghỉ phép / Truyền thông / Tài sản / Cài đặt
  {
    key: 'employees',
    module: 'employees',
    label: msg`Nhân sự`,
    icon: Users,
    basePath: '/employees',
    items: [
      { key: 'employees-list', label: msg`Nhân viên`, path: '/employees', group: 'Nhân sự', permission: 'employees_list:view' },
      { key: 'employees-invitations', label: msg`Lời mời nhân viên`, path: '/users/invitations', group: 'Nhân sự', permission: 'employees_invitations:view' },
      { key: 'employees-events', label: msg`Sự kiện`, path: '/employees/events', group: 'Nhân sự', permission: 'employees_events:view' },
      { key: 'teams', label: msg`Team / Phòng ban`, path: '/teams', group: 'Nhân sự', permission: 'teams:view' },
      { key: 'org-chart', label: msg`Sơ đồ tổ chức`, path: '/org-chart', group: 'Nhân sự', permission: 'org_chart:view' },
      { key: 'employees-contracts', label: msg`Hợp đồng`, path: '/employees/contracts', group: 'Hợp đồng', permission: 'employees_contracts:view' },
      { key: 'employees-contract-types', label: msg`Loại hợp đồng`, path: '/employees/contract-types', group: 'Hợp đồng', permission: 'employees_contract_types:view' },
      { key: 'employees-job-titles', label: msg`Danh mục chức vụ`, path: '/employees/job-titles', group: 'Hợp đồng', permission: 'employees_contracts:view' },
      // Nhóm "Lương" — ẨN LẠI 07/10/2026, sếp Phú: «ẩn tính năng liên quan lương trên saas đi, hệ này làm chưa xong».
      // Ẩn cùng đợt: route /payroll/* + /profile/payroll (App.jsx) · lối tắt «Bảng lương cá nhân» ở Tài khoản
      // (cnv-ui `tenant.features.payroll`) · bảng Phân quyền tự mất dòng lương vì derive từ menu. API
      // `modules/payroll` GIỮ NGUYÊN. Bật lại = bỏ chú thích 5 dòng dưới + gỡ các dòng nợ ghi «Lương ẩn 07/10»
      // ở tools/check-port-parity.js · check-ghost-write-permissions.js (lịch sử mở lại lần trước: 27e29e7c).
      // ⚠️ Cả 5 gate bằng `payroll:view` — Work SaaS chỉ seed MỘT module `payroll`; đừng chép gate nhỏ của Core.
      // { key: 'payroll-periods', label: msg`Bảng lương`, path: '/payroll/periods', group: 'Lương', permission: 'payroll:view' },
      // { key: 'payroll-structures', label: msg`Khung lương NV`, path: '/payroll/structures', group: 'Lương', permission: 'payroll:view' },
      // { key: 'payroll-levels', label: msg`Bậc cấp`, path: '/payroll/levels', group: 'Lương', permission: 'payroll:view' },
      // { key: 'payroll-insurance', label: msg`BHXH/BHYT/BHTN`, path: '/payroll/insurance', group: 'Lương', permission: 'payroll:view' },
      // { key: 'payroll-tax', label: msg`Thuế TNCN`, path: '/payroll/tax', group: 'Lương', permission: 'payroll:view' },
      { key: 'attendance-list', label: msg`Quản lý chấm công`, path: '/attendance', group: 'Chấm công', permission: 'attendance_list:view' },
      { key: 'timesheet', label: msg`Bảng chấm công (tuần)`, path: '/timesheet', group: 'Chấm công', permission: 'timesheet:view' },
      { key: 'timesheet-monthly', label: msg`Bảng chấm công (tháng)`, path: '/timesheet/monthly', group: 'Chấm công', permission: 'timesheet_monthly:view' },
      { key: 'shifts', label: msg`Ca làm việc`, path: '/shifts', group: 'Chấm công', permission: 'shifts:view' },
      { key: 'shifts-credit-rules', label: msg`Quy tắc tính công`, path: '/shifts/credit-rules', group: 'Chấm công', permission: 'shifts:view' },
      { key: 'corrections', label: msg`Duyệt điều chỉnh`, path: '/corrections', group: 'Chấm công', permission: 'corrections:view' },
      { key: 'overtime', label: msg`Tăng ca`, path: '/overtime', group: 'Chấm công', permission: 'attendance_list:view' },
      { key: 'overtime-policy', label: msg`Chính sách tăng ca`, path: '/overtime/policy', group: 'Chấm công', permission: 'attendance_list:update' },
      // Lịch tuần (quản lý) — cùng quyền «Ca làm việc» y Core; màn nhân viên «Lịch làm việc và ca trống»
      // (/my-roster) không có mục menu, vào từ trang cá nhân (cnv-ui AccountSection).
      { key: 'roster', label: msg`Lịch tuần`, path: '/roster', group: 'Chấm công', permission: 'shifts:view' },
      { key: 'leaves', label: msg`Nghỉ phép`, path: '/leaves', group: 'Nghỉ phép', permission: 'leaves:view' },
      { key: 'leave-balance', label: msg`Phép năm`, path: '/leave-balance', group: 'Nghỉ phép', permission: 'leave_balance:view' },
      { key: 'holidays', label: msg`Ngày nghỉ lễ`, path: '/holidays', group: 'Nghỉ phép', permission: 'holidays:view' },
      { key: 'assets-list', label: msg`Danh sách tài sản`, path: '/assets', group: 'Tài sản', permission: 'assets:view' },
      { key: 'assets-assignments', label: msg`Giao & Thu hồi`, path: '/assets/assignments', group: 'Tài sản', permission: 'asset_assignments:view' },
      { key: 'assets-audit', label: msg`Kiểm kê`, path: '/assets/audit', group: 'Tài sản', permission: 'asset_audit:view' },
      { key: 'digital-assets-list', label: msg`Danh sách`, path: '/digital-assets', group: 'Tài sản số', permission: 'digital_assets_list:view' },
      { key: 'digital-assets-billing', label: msg`Theo dõi phí`, path: '/digital-assets/billing', group: 'Tài sản số', permission: 'digital_assets_billing:view' },
      { key: 'digital-assets-alerts', label: msg`Cảnh báo`, path: '/digital-assets/alerts', group: 'Tài sản số', permission: 'digital_assets_alerts:view' },
      { key: 'penalties', label: msg`Phạt đi trễ`, path: '/penalties', group: 'Cài đặt', permission: 'penalties:view' },
      { key: 'locations', label: msg`Địa điểm`, path: '/locations', group: 'Cài đặt', permission: 'locations:view' },
    ],
  },

  // ===== Inbox — clone NGUYÊN từ attendance-app/admin/src/navigation/adminMenu.js (line 56-72) =====
  {
    key: 'chat',
    module: 'chat',
    label: msg`Inbox`,
    icon: MessageCircle,
    basePath: '/inbox',
    // RBAC 1:1 — mỗi sub-menu gate bằng fine module riêng (inbox_*), seed giống
    // Core. admin BYPASS; non-admin nhận qua backfill (role có 'inbox' → 9 fine).
    items: [
      { key: 'chat-inbox', label: msg`Hộp thư`, path: '/inbox', permission: 'inbox_messages:view' },
      { key: 'chat-canned', label: msg`Tin nhắn mẫu`, path: '/inbox/canned-responses', permission: 'inbox_canned:view' },
      // SaaS-only (Core KHÔNG có) — khai trong ALLOWED_EXTRAS của tools/check-port-parity.js.
      // Không đặt vào nhóm con 'Cài đặt': đây là việc làm hằng ngày, không phải cấu hình một lần.
      { key: 'chat-zalo-campaigns', label: msg`Gửi tin vào nhóm Zalo`, path: '/inbox/zalo-campaigns', permission: 'inbox_zalo_campaigns:view' },
      { key: 'chat-agents', label: msg`Agents`, path: '/inbox/agents', group: 'AI', permission: 'inbox_agents:view' },
      { key: 'chat-kb', label: msg`Tài liệu`, path: '/inbox/knowledge-base', group: 'AI', permission: 'inbox_kb:view' },
      // Tách 1 trang 2 tab thành 2 mục cho dễ hiểu; DÙNG CHUNG quyền inbox_training (không đẻ quyền mới).
      { key: 'chat-ai-assist', label: msg`Trợ giúp AI`, path: '/inbox/ai-assist', group: 'AI', permission: 'inbox_training:view' },
      { key: 'chat-training', label: msg`Huấn luyện`, path: '/inbox/ai-training', group: 'AI', permission: 'inbox_training:view' },
      { key: 'chat-ai-logs', label: msg`Logs & Chi phí`, path: '/inbox/ai-logs', group: 'AI', permission: 'inbox_ai_logs:view' },
      { key: 'chat-settings', label: msg`Kênh`, path: '/inbox/channels', group: 'Cài đặt', permission: 'inbox_channels:view' },
      { key: 'chat-staff', label: msg`Nhân viên trong Zalo`, path: '/inbox/staff', group: 'Cài đặt', permission: 'inbox_staff:view' },
      { key: 'chat-config', label: msg`Cài đặt`, path: '/inbox/config', group: 'Cài đặt', permission: 'inbox_config:view' },
    ],
  },

  // ===== Chat (Slack/Lark-style) — clone 1:1 từ attendance-app =====
  // Core: { key:'team_chat', label:'Chat', icon:MessagesSquare, path:'/chat' } — direct
  // path, KHÔNG có submenu/items[]. Gate `privateModule` (SaaS) = `private:true` (Core).
  {
    key: 'team_chat',
    module: 'team_chat',
    label: msg`Chat`,
    icon: MessagesSquare,
    path: '/chat',
    privateModule: 'team_chat',
  },

  // ===== CRM — clone 1:1 từ Core's adminMenu.js (line 89-123) =====
  // RULE (CLAUDE.md feedback "ko dc chế flow ... 100% theo flow và kể cả cách SẮP XẾP menu"):
  // - KHÔNG tự thêm/bớt items (Core ko có /crm/pipeline, /crm/coupons trong menu)
  // - KHÔNG đổi tên ("Đơn bán hàng" KHÔNG phải "Hóa đơn", "Subscriptions" KHÔNG phải "Đăng ký",
  //   "Phương thức thanh toán" KHÔNG viết tắt)
  // - "Khuyến mãi" là group RIÊNG (KHÔNG nested trong Tài chính)
  // Items chưa có page sẽ render Placeholder "TODO: port from Core" để anh thấy gap.
  {
    key: 'crm',
    module: 'crm',
    label: 'CRM',
    icon: Target,
    basePath: '/crm',
    items: [
      // ===== CRM (Tổng quan + Deals) =====
      { key: 'crm-dashboard', label: msg`Tổng quan`, path: '/crm', group: 'CRM', permission: 'crm_dashboard:view' },
      { key: 'crm-deals', label: msg`Deals`, path: '/crm/deals', group: 'CRM', permission: 'crm_deals:view' },

      // ===== Khách hàng =====
      { key: 'crm-companies', label: msg`Công ty`, path: '/crm/companies', group: 'Khách hàng', permission: 'crm_companies:view' },
      { key: 'crm-contacts', label: msg`Liên hệ`, path: '/crm/contacts', group: 'Khách hàng', permission: 'crm_contacts:view' },
      { key: 'crm-leads', label: msg`Leads`, path: '/crm/leads', group: 'Khách hàng', permission: 'crm_leads:view' },
      { key: 'crm-customers', label: msg`Khách hàng`, path: '/crm/customers', group: 'Khách hàng', permission: 'crm_customers:view' },

      // ===== Vận hành =====
      // Thứ tự = Core adminMenu.js:107-113 (bỏ 'meetings-list' — CNV-only, xem PORT_GAPS.md).
      // «Hoạt động» mang context riêng: cnv-ui dùng CÙNG msgid làm trạng thái bật/tắt (EN "Active"),
      // chung khoá là menu CRM hiện "Active" thay vì danh sách hoạt động. Ở đây dịch "Activities".
      { key: 'crm-activities', label: msg({ context: 'host', message: `Hoạt động` }), path: '/crm/activities', group: 'Vận hành', permission: 'crm_activities:view' },
      { key: 'crm-routines', label: msg`Nhịp công việc`, path: '/crm/routines', group: 'Vận hành', permission: 'crm_routines:view' },
      { key: 'crm-lead-claims', label: msg`Yêu cầu bàn giao lead`, path: '/crm/lead-claim-requests', group: 'Vận hành', permission: 'crm_leads:view' },
      // Đơn THAM GIA (đồng sở hữu liên hệ/công ty) — KHÁC bàn giao lead: không chuyển sở hữu.
      // Quyền `crm_contacts:view` khớp Core crm.js:1322 (Core dùng crm_contacts cho cả đơn công ty).
      { key: 'crm-access-requests', label: msg`Yêu cầu tham gia liên hệ / công ty`, path: '/crm/access-requests', group: 'Vận hành', permission: 'crm_contacts:view' },
      { key: 'crm-documents', label: msg`Tài liệu`, path: '/crm/documents', group: 'Vận hành', permission: 'crm_documents:view' },
      { key: 'crm-data-transfer', label: msg`Chuyển giao dữ liệu`, path: '/crm/data-transfer', group: 'Vận hành', permission: 'crm_data_transfer:view' },

      // ===== Tài chính =====
      { key: 'crm-products', label: msg`Sản phẩm`, path: '/crm/products', group: 'Tài chính', permission: 'crm_products:view' },
      { key: 'crm-quotations', label: msg`Báo giá`, path: '/crm/quotations', group: 'Tài chính', permission: 'crm_quotations:view' },
      { key: 'crm-invoices', label: msg`Đơn bán hàng`, path: '/crm/invoices', group: 'Tài chính', permission: 'crm_invoices:view' },
      { key: 'crm-payments', label: msg`Thanh toán`, path: '/crm/payments', group: 'Tài chính', permission: 'crm_payments:view' },
      { key: 'crm-targets', label: msg`Chỉ tiêu`, path: '/crm/targets', group: 'Tài chính', permission: 'crm_targets:view' },
      { key: 'crm-credit-notes', label: msg`Phiếu điều chỉnh`, path: '/crm/credit-notes', group: 'Tài chính', permission: 'crm_credit_notes:view' },
      { key: 'crm-subscriptions', label: msg`Subscriptions`, path: '/crm/subscriptions', group: 'Tài chính', permission: 'crm_subscriptions:view' },

      // ===== Khuyến mãi (group RIÊNG) =====
      { key: 'crm-promotions', label: msg`Khuyến mãi`, path: '/crm/promotions', group: 'Khuyến mãi', permission: 'crm_promotions:view' },

      // ===== Cài đặt =====
      { key: 'crm-product-categories', label: msg`Danh mục sản phẩm`, path: '/crm/product-categories', group: 'Cài đặt', permission: 'crm_product_categories:view' },
      { key: 'crm-lead-objectives', label: msg`Mục tiêu bán hàng`, path: '/crm/lead-objectives', group: 'Cài đặt', permission: 'crm_lead_objectives:view' },
      { key: 'crm-payment-methods', label: msg`Phương thức thanh toán`, path: '/crm/payment-methods', group: 'Cài đặt', permission: 'crm_payment_methods:view' },
      { key: 'crm-roundrobin', label: msg`Round-Robin`, path: '/crm/round-robin', group: 'Cài đặt', permission: 'crm_roundrobin:view' },
      { key: 'crm-deal-roles', label: msg`Vai trò Deal`, path: '/crm/deal-roles', group: 'Cài đặt', permission: 'crm_deal_roles:view' },
      { key: 'crm-doc-templates', label: msg`Mẫu tài liệu`, path: '/crm/doc-templates', group: 'Cài đặt', permission: 'crm_doc_templates:view' },
      { key: 'crm-quotation-approval', label: msg`Duyệt báo giá`, path: '/crm/quotation-approval', group: 'Cài đặt', permission: 'crm_quotation_approval:view' },
      { key: 'crm-invoice-billing', label: msg`Thông tin xuất HĐ`, path: '/crm/invoice-billing-settings', group: 'Cài đặt', permission: 'crm_invoice_billing:view' },
      // SaaS-only (ALLOWED_EXTRAS): nâng cấp «Trường bắt buộc Lead» → «Trường dữ liệu» (trường tùy chỉnh CRM, 02/10/2026).
      { key: 'crm-lead-fields', label: msg`Trường dữ liệu`, path: '/crm/fields', group: 'Cài đặt', permission: 'crm_lead_fields:view' },
      { key: 'crm-data-transfer-settings', label: msg`Cài đặt chuyển giao`, path: '/crm/data-transfer-settings', group: 'Cài đặt', permission: 'crm_data_transfer_settings:view' },
      { key: 'crm-industries', label: msg`Ngành nghề`, path: '/crm/industries', group: 'Cài đặt', permission: 'crm_industries:view' },
      { key: 'crm-sources', label: msg`Nguồn`, path: '/crm/sources', group: 'Cài đặt', permission: 'crm_sources:view' },
      { key: 'duplicate-check', label: msg`Kiểm tra trùng lặp`, path: '/crm/duplicate-check', group: 'Cài đặt', permission: 'duplicate_check:view' },
      // Quyền dùng CHÍNH `crm_leads:view` — khớp gate BE ở leadScoringV1.js. KHÔNG bịa key mới:
      // key không có trong bảng permissions thì menu tự ẩn với mọi người không phải admin.
      { key: 'crm-lead-scoring', label: msg`Chấm điểm lead`, path: '/crm/lead-scoring', group: 'Cài đặt', permission: 'crm_leads:view' },
    ],
  },

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Thứ tự nhóm dưới đây ĐỒNG NHẤT với Core adminMenu.js — 2026-05-23.
  // Khi sửa Core thì sync SaaS giữ thống nhất UX.
  // Order: support → tasks → approvals → marketing → utilities → reports →
  //        recruitment → automation → (finance/CNV-only) → system → marketplace
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  // ===== Hỗ trợ (Ticket/Help desk) — anh chốt 2026-05-17: canonical /tickets/*.
  // Module BE folder name vẫn "support" (namespace nội bộ), URL path = /tickets/*.
  {
    key: 'support',
    module: 'support',
    label: msg`Hỗ trợ`,
    icon: LifeBuoy,
    basePath: '/tickets',
    items: [
      { key: 'support-tickets', label: msg`Ticket`, path: '/tickets', permission: 'support_tickets:view' },
      { key: 'support-dashboard', label: msg`Dashboard`, path: '/tickets/dashboard', permission: 'support_dashboard:view' },
      { key: 'support-categories', label: msg`Loại hỗ trợ`, path: '/tickets/categories', group: 'Cài đặt', permission: 'ticket_categories:view' },
      { key: 'support-sla', label: 'SLA', path: '/tickets/sla', group: 'Cài đặt', permission: 'ticket_sla:view' },
      { key: 'support-rules', label: msg`Phân công`, path: '/tickets/rules', group: 'Cài đặt', permission: 'ticket_rules:view' },
      { key: 'support-templates', label: msg`Mẫu phản hồi`, path: '/tickets/canned-responses', group: 'Cài đặt', permission: 'support_templates:view' },
      { key: 'support-widget', label: msg`Widget`, path: '/tickets/widget', group: 'Cài đặt', permission: 'support_widget:view' },
      { key: 'support-settings', label: msg`Cài đặt`, path: '/tickets/settings', group: 'Cài đặt', permission: 'support_settings:view' },
    ],
  },

  // ===== Công việc (Tasks) — clone 1:1 từ Core adminMenu (key='tasks') =====
  // Standard menu items minimal (1 item) — TasksSecondaryNav custom 240px sidebar render full UI
  // ===== Dự án — port 1:1 từ CNV Connect 28/08/2026 =====
  // Năm mục là năm CÁCH NHÌN cùng một tập dữ liệu (`task_projects`), không phải năm màn rời.
  // Trên chính trang có dãy icon chuyển qua lại (menu con ở thanh trái — dãy ngang đã bỏ 03/09) nên nav phụ
  // của cụm này TẮT — xem DETAIL_PATTERNS ở cuối tệp.
  //
  // Nhãn cụm này BỌC `msg` — khác 100% nhãn menu còn lại của Work (đang là chuỗi Việt thường,
  // tức khách EN/FR thấy menu tiếng Việt: nợ cũ, không giải trong lượt port này).
  // Bọc được vì hàng rào đã có: mọi chỗ đọc nhãn menu trong cnv-ui đều đi qua `tLabel(v)`
  // (`PermissionsPage` dòng 1148/1166, `SidebarPrimary` dòng 83) — descriptor thì `i18n._()`,
  // chuỗi thường thì trả nguyên si. Đã grep: KHÔNG còn chỗ nào render `{menu.label}` thô.
  // Thiếu hàng rào đó chính là thứ từng làm trắng nguyên trang /permissions bên Connect.
  {
    key: 'projects',
    module: 'tasks',
    // BẬT LẠI 30/09/2026 — cụm từng TẠM ẨN (3bfc0bf3, 24/09) trong lúc Site Connect làm lại mô hình «job = dự án,
    // ca = việc». Máy chủ Work đã theo mô hình mới (migration 1797500000000: mỗi ca là một việc) nên gỡ cờ ẩn.
    label: msg`Dự án`,
    icon: Building2,
    basePath: '/projects',
    items: [
      { key: 'projects-list',        label: msg`Danh sách`,       path: '/projects',            permission: 'tasks:view' },
      { key: 'projects-board',       label: msg`Bảng giai đoạn`,  path: '/projects/board',      permission: 'tasks:view' },
      { key: 'projects-map',         label: msg`Bản đồ`,          path: '/projects/map',        permission: 'tasks:view' },
      // Hai bản đồ trả lời hai câu khác nhau: «Bản đồ» = dự án nằm ở đâu · «Bản đồ check-in»
      // = người đang ở đâu. Thẻ cũ trong Kho báo cáo đã ẩn (HIDDEN_REPORT_KEYS ở App.jsx) để
      // một trang chỉ có MỘT đường vào.
      { key: 'projects-checkin-map', label: msg`Bản đồ check-in`, path: '/projects/checkin-map', permission: 'tasks:view' },
      // «Xếp việc» đặt CÙNG nhóm dự án chứ không phải nhóm Nhân sự: nó xếp NGƯỜI vào DỰ ÁN, và
      // người mở nó ra là người đang nhìn danh sách dự án. Cụm chấm công có «Lịch làm việc»
      // trả lời câu khác — ai làm ca nào — xem đầu migration 1790980000000 về vì sao hai bảng.
      // Quyền RIÊNG «Xếp việc» (30/09/2026) — `work_schedule:view`. MẶC ĐỊNH AI CŨNG THẤY (sếp Phú 30/09 tối: «Menu xếp
      // việc là mặc định phải show all» — mẫu `employee` có ô Xem, migration 1797800000000): người chỉ Xem mở bàn chỉ
      // đọc, thấy lịch ĐÃ GỬI; ca nháp / tiền công là của người có ô Sửa (máy chủ lọc). Trước 30/09 là `tasks:view`.
      { key: 'work-schedule',        label: msg`Xếp việc`,        path: '/schedule',            permission: 'work_schedule:view' },
      // Cuối nhóm, khớp CNV Connect. Chứa công tắc «Múi giờ đa vùng» — chỗ DUY NHẤT bật nó
      // (sếp Phú 03/09: setting của dự án thì để ở dự án).
      // Hai khuôn dùng lại — rút khỏi dãy ngang 03/09 (nó đã 7 ô và lặp lại menu này).
      { key: 'projects-templates',   label: msg`Mẫu dự án`,       path: '/projects/templates',      permission: 'tasks:view' },
      { key: 'projects-form-templates', label: msg`Mẫu biểu mẫu`, path: '/projects/form-templates', permission: 'tasks:view' },
      { key: 'projects-settings',    label: msg`Cài đặt`,         path: '/projects/settings',   permission: 'tasks:view' },
    ],
  },

  {
    key: 'tasks',
    module: 'tasks',
    label: msg`Công việc`,
    icon: CheckSquare,
    basePath: '/tasks',
    items: [
      { key: 'tasks-home', label: msg`Nhiệm vụ`, path: '/tasks', permission: 'tasks:view' },
      // SaaS-only (10/09/2026): quản lý việc theo nhịp thời gian kiểu sprint. Core chưa có —
      // khai ở tools/check-port-parity.js ALLOWED_EXTRAS + docs/PORT_GAPS.md.
      { key: 'tasks-circles', label: msg`Circle`, path: '/tasks/circles', permission: 'tasks:view' },
    ],
  },

  // ===== Phê duyệt (Approvals) — clone 1:1 từ Core adminMenu (key='approvals') =====
  {
    key: 'approvals',
    module: 'approvals',
    label: msg`Phê duyệt`,
    icon: FileCheck2,
    basePath: '/approvals',
    items: [
      { key: 'approvals-inbox', label: msg`Hộp thư phê duyệt`, path: '/approvals', permission: 'approvals:view' },
      { key: 'approval-templates', label: msg`Mẫu phê duyệt`, path: '/approvals/templates', permission: 'approval_templates:view' },
    ],
  },

  // ===== Marketing — clone 1:1 Core adminMenu (key='marketing') =====
  {
    key: 'marketing',
    module: 'marketing',
    label: msg`Marketing`,
    icon: Target,
    basePath: '/marketing',
    items: [
      { key: 'marketing-dashboard', label: msg`Tổng quan`, path: '/marketing', permission: 'marketing_dashboard:view' },
      { key: 'marketing-campaigns', label: msg`Chiến dịch`, path: '/marketing/campaigns', permission: 'marketing_campaigns:view' },
      // «Lead Inbox (Audit)» gộp vào Lead Forms 2026-08-05 — mirror Core adminMenu.js.
      { key: 'marketing-lead-forms', label: msg`Lead Forms`, path: '/marketing/lead-forms', permission: 'marketing_lead_forms:view' },
      { key: 'marketing-events', label: msg`Events (Hội thảo)`, path: '/marketing/events', permission: 'marketing_events:view' },
      { key: 'marketing-memberships', label: msg`CLB / Hiệp hội`, path: '/marketing/memberships', permission: 'marketing_memberships:view' },
      { key: 'marketing-ad-accounts', label: msg`Tài khoản QC`, path: '/marketing/ad-accounts', group: 'Cài đặt', permission: 'marketing_ad_accounts:view' },
      // 'marketing-audiences' (Ads Audience) — XOÁ hẳn 2026-08-05 (user yêu cầu). FE+BE gỡ, bảng DB giữ.
    ],
  },

  // ===== Tuyển dụng (Recruitment) — clone 1:1 Core adminMenu (key='recruitment') =====
  {
    key: 'recruitment',
    module: 'recruitment',
    label: msg`Tuyển dụng`,
    icon: Briefcase,
    basePath: '/recruitment',
    items: [
      { key: 'recruitment-dashboard', label: msg`Tổng quan`, path: '/recruitment', permission: 'recruitment_dashboard:view' },
      { key: 'recruitment-proposals', label: msg`Đề xuất`, path: '/recruitment/proposals', permission: 'recruitment_proposals:view' },
      { key: 'recruitment-campaigns', label: msg`Chiến dịch`, path: '/recruitment/campaigns', permission: 'recruitment_campaigns:view' },
      { key: 'recruitment-candidates', label: msg`Ứng viên`, path: '/recruitment/candidates', permission: 'recruitment_candidates:view' },
      { key: 'recruitment-jd', label: msg`Thư viện JD`, path: '/recruitment/jd-library', permission: 'recruitment_jd:view' },
      { key: 'recruitment-onboarding', label: msg`Onboarding`, path: '/recruitment/onboarding', permission: 'recruitment_onboarding:view' },
      { key: 'recruitment-offboarding', label: msg`Offboarding`, path: '/recruitment/offboarding', permission: 'recruitment_offboarding:view' },
      { key: 'recruitment-sources', label: msg`Nguồn ứng viên`, path: '/recruitment/sources', permission: 'recruitment_sources:view' },
      // Cài đặt CHUNG của khu Tuyển dụng (nhóm đầu: AI đọc CV). Gác bằng quyền SỬA chiến dịch:
      // ai lập được chiến dịch thì mới chỉnh được cách chấm điểm (khớp gate BE).
      // Nhãn BỌC `msg` như cụm Dự án (mọi chỗ đọc nhãn menu đều qua `tLabel()`).
      { key: 'recruitment-settings', label: msg`Cài đặt`, path: '/recruitment/settings', permission: 'recruitment_campaigns:update' },
    ],
  },

  // ===== Automation — single-link (KHÔNG có items, giống Reports) — clone 1:1 Core =====
  // LƯU Ý: KHÔNG để `items: []` — filter visibleMenu cuối loại group có items rỗng → mất menu.
  {
    key: 'automation',
    module: 'automation',
    label: msg`Automation`,
    icon: Zap,
    basePath: '/automation',
    path: '/automation',
  },

  // ===== Tài chính (Finance) — TẠM ẨN. Core có duy nhất Tên miền (466 LOC FE + 159 LOC BE),
  // SaaS chưa port → render placeholder gây UX gãy (Bug #5 2026-05-14). Document trong
  // docs/PORT_GAPS.md, bật lại khi port full module.
  // {
  //   key: 'finance',
  //   module: 'domains',
  //   label: 'Tài chính',
  //   icon: Landmark,
  //   basePath: '/finance',
  //   items: [
  //     { key: 'finance-domains', label: 'Tên miền', path: '/finance/domains' },
  //   ],
  // },

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Cuối menu: Báo cáo → Tiện ích → Hệ thống (anh chốt thứ tự 2026-05-24).
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  // ===== Reports — TOP-LEVEL single-link group. Page tự có tabs filter theo module.
  {
    key: 'reports',
    label: msg`Báo cáo`,
    icon: BarChart3,
    path: '/reports',
  },

  // ===== Tiện ích — Nhà cung cấp + Kho in ấn =====
  {
    key: 'utilities',
    module: 'utilities',
    label: msg`Tiện ích`,
    icon: Wrench,
    basePath: '/utilities',
    items: [
      { key: 'utilities-suppliers', label: msg`Nhà cung cấp`, path: '/utilities/suppliers', permission: 'suppliers:view' },
      // Xuất file (Export Center) — SaaS-only tới khi port sang Core (chunk 4, docs/EXPORT_CENTER.md).
      // Đặt ở Tiện ích vì nó phục vụ MỌI module: không thuộc CRM/Nhân sự, cũng không phải khu admin
      // ("Hệ thống") vì nhân viên vẫn phải vào lấy file của chính mình.
      { key: 'utilities-exports', label: msg`Xuất file`, path: '/utilities/exports', permission: 'exports:view' },
      { key: 'utilities-print-library', label: msg`Kho in ấn`, path: '/utilities/print-library', permission: 'print_library:view' },
      { key: 'utilities-noti-templates', label: msg`Mẫu thông báo`, path: '/utilities/notification-templates', permission: 'noti_templates:view' },
      // Email Template — port 1:1 Core (adminMenu.js line 286): moved từ "Hệ thống" → "Tiện ích" (2026-07-03).
      // GIỮ path /settings/email-templates vì page shared cnv-ui (EmailTemplatesPage) hardcode nav
      // /settings/email-templates/:id — đổi path sẽ vỡ nav (giống tiền lệ int-bot).
      { key: 'email-templates', label: msg`Email Template`, path: '/settings/email-templates', permission: 'email_templates:view' },
      // Chữ ký email — port 1:1 Core (adminMenu.js): thiết kế chữ ký theo «Mẫu công ty» rồi dán vào
      // Gmail. Trang shared @cnv/ui, luật dữ liệu ở kernel emailSignature. `view` tới mọi vai trò hệ
      // thống qua template (+ vai trò tự tạo ở migration 1796400000000); sửa mẫu công ty cần `update`.
      { key: 'utilities-email-signature', label: msg`Chữ ký email`, path: '/utilities/email-signature', permission: 'email_signature:view' },
      { key: 'utilities-company-room', label: msg`Nhóm chat chung`, path: '/utilities/company-room', permission: 'company_room:view' },
      // Dịch cabin — port 1:1 Core (adminMenu.js): phiên dịch đồng thời cho họp trực tiếp
      // và họp online. Trang shared @cnv/ui, hằng số shared @cnv/server-shared. Gate bằng
      // quyền `cabin_translate:view`; MỞ PHIÊN cần thêm `cabin_translate:create` (BE gác) — tách
      // hai khoá vì mở phiên là tiêu tiền theo phút audio.
      { key: 'utilities-cabin-translate', label: msg`Dịch cabin`, path: '/utilities/cabin-translate', permission: 'cabin_translate:view' },
      // Thư viện Pipeline — port 1:1 Core: moved từ "Hệ thống" → "Tiện ích" (2026-07-15).
      // Path /settings/pipelines đúng như Core (adminMenu.js:297). Trước 2026-08-04 SaaS để
      // /recruitment/pipelines với lý do "đổi path sẽ vỡ nav shared cnv-ui" — NGƯỢC: chính path
      // lệch đó làm vỡ nav, vì page shared điều hướng cứng sang /settings/pipelines/*. Route
      // /recruitment/pipelines/* vẫn giữ làm alias trong App.jsx (Core cũng mount cả hai).
      { key: 'system-pipelines', label: msg`Thư viện Pipeline`, path: '/settings/pipelines', permission: 'recruitment_pipelines:view' },
      // Giao diện Kanban — moved từ Hệ thống sang Tiện ích 2026-08-17 (sếp Phú chốt), khớp Core.
      // GIỮ path /settings/kanban-theme: page shared cnv-ui, đổi path là vỡ nav + lệch Core.
      // Dùng lại quyền `settings`, KHÔNG đẻ module quyền mới (BE gate ghi bằng settings:update).
      { key: 'kanban-theme', label: msg`Giao diện Kanban`, path: '/settings/kanban-theme', permission: 'settings:view' },
      // Bot tenant (webhook gửi/nhận tin) — port 1:1 Core (adminMenu.js line 279):
      // nằm trong "Tiện ích", gate theo team_chat. GIỮ path /settings/bots (page
      // shared cnv-ui hardcode nav /settings/bots/:id).
      { key: 'int-bot', label: msg`Bot`, path: '/settings/bots', permission: 'int_bot:view', privateModule: 'team_chat' },
    ],
  },

  // ===== System — port 1:1 từ Core's adminMenu.js (line 274-307), filter Mattermost +
  // CNV-only items. SaaS thêm API Keys + Webhooks vào `Tích hợp`. =====
  {
    key: 'system',
    label: msg`Hệ thống`,
    icon: Settings,
    basePath: '/settings',
    items: [
      // Cài đặt
      { key: 'settings',          label: msg`Cài đặt chung`,     path: '/settings',                          group: 'Cài đặt', permission: 'settings:view' },
      // Email Template ĐÃ CHUYỂN sang menu "Tiện ích" (port 1:1 Core adminMenu.js, 2026-07-03).
      { key: 'notification-logs', label: msg`Lịch sử thông báo`, path: '/settings/notification-logs',        group: 'Cài đặt', permission: 'notification_logs:view' },
      { key: 'ai-usage',          label: msg`Chi phí AI`,        path: '/settings/ai-usage',                 group: 'Cài đặt', permission: 'ai_usage:view' },
      // SaaS-only — công tắc «Tắt toàn bộ AI» cấp workspace (AI-01). Dùng lại quyền `settings` (xem), KHÔNG đẻ module
      // quyền mới; BE ghi bằng requireTenantAdmin. docs/PORT_GAPS.md 2026-10-20.
      { key: 'ai-settings',       label: msg`Cài đặt AI`,        path: '/settings/ai',                       group: 'Cài đặt', permission: 'settings:view' },
      // Tích hợp — bên thứ 3 + SaaS-only (API Keys, Webhooks)
      // Zalo + Facebook: kết nối Page/OA đã có ở /inbox/channels — bỏ trang riêng,
      // SaaS dùng chung 1 App ở system-level (ENV), user không cần nhập key (2026-05-12)
      { key: 'int-claude',        label: 'Claude',            path: '/settings/integrations/claude',      group: 'Tích hợp', permission: 'int_claude:view' },
      { key: 'int-microsoft',     label: 'Microsoft 365 / Entra', path: '/settings/integrations/microsoft', group: 'Tích hợp', permission: 'int_microsoft:view' },
      { key: 'int-chatgpt',       label: 'ChatGPT',           path: '/settings/integrations/chatgpt',     group: 'Tích hợp', permission: 'int_chatgpt:view' },
      { key: 'google-settings',   label: 'Google',            path: '/settings/google',                   group: 'Tích hợp', permission: 'int_google:view' },
      // Fireflies bỏ khỏi SaaS (2026-07-11) — Core attendance-app vẫn dùng.
      { key: 'int-cnv-call',      label: 'CNV Call',          path: '/settings/integrations/cnv-call',    group: 'Tích hợp', permission: 'int_cnv_call:view' },
      // CNV Chat bỏ khỏi SaaS (2026-07-25) — SaaS tự đăng ký tenant với chat-service khi
      // provisioning, không cần trang setup thủ công. Core attendance-app vẫn dùng.
      // Bot ĐÃ CHUYỂN sang group "Tiện ích" (port 1:1 Core adminMenu.js).
      { key: 'api-keys',          label: msg`API Keys`,          path: '/api-keys',                          group: 'Tích hợp', permission: 'api_keys:view' },
      { key: 'mcp-keys',          label: msg`Kết nối MCP`,       path: '/mcp-keys',                          group: 'Tích hợp', permission: 'api_keys:view' },
      { key: 'webhooks',          label: msg`Webhooks`,          path: '/webhooks',                          group: 'Tích hợp', permission: 'webhooks:view' },
      // Hệ thống
      { key: 'roles',             label: msg`Vai trò`,           path: '/roles',                             group: 'Hệ thống', permission: 'roles:view' },
      { key: 'permissions',       label: msg`Phân quyền`,        path: '/permissions',                       group: 'Hệ thống', permission: 'permissions:view' },
      // SaaS-only — nhật ký kiểm toán (form bảo mật khách BE). Dùng lại quyền `settings` (xem), KHÔNG đẻ module quyền
      // mới; BE đọc bằng requireTenantAdmin. docs/PORT_GAPS.md 2026-10-20.
      { key: 'audit-log',         label: msg`Nhật ký kiểm toán`, path: '/settings/audit-log',                group: 'Hệ thống', permission: 'settings:view' },
      { key: 'storage-usage',     label: msg`Dung lượng`,        path: '/settings/storage',                  group: 'Hệ thống', permission: 'storage:view_all' },
      // SaaS-only (Core bán self-hosted, không có chỗ ngồi để chia) — docs/PORT_GAPS.md.
      // Dùng lại quyền `settings`, KHÔNG đẻ module quyền mới; BE gate bằng requireTenantAdmin.
      { key: 'feature-licenses',  label: msg`Bản quyền tính năng`, path: '/settings/feature-licenses',       group: 'Hệ thống', permission: 'settings:view' },
      // Thư viện Pipeline ĐÃ CHUYỂN sang menu "Tiện ích" (port 1:1 Core adminMenu.js, 2026-07-15).
      // SaaS-only — 1 user thuộc nhiều workspace. Core không có vì single-tenant.
      { key: 'switch-workspace',  label: msg`Đổi workspace`,     path: '/select-workspace',                  group: 'Hệ thống' },
    ],
  },

  // ===== Marketplace — SaaS-ONLY (Core không có). Marketplace module = catalog tự catalog =====
  // Documented trong docs/PORT_GAPS.md
  {
    key: 'marketplace',
    module: 'marketplace',
    // Nhãn KHÔNG còn là "Marketplace"/"Kho ứng dụng" (sếp Phú 29.08.2026): trang này đã gom cả
    // phần Billing vào, nên nó là chỗ xem gói + mua + trả tiền, không phải cửa hàng ứng dụng.
    //
    // ⚠️ Thanh dọc chỉ vừa ~10 KÝ TỰ (bằng "Tuyển dụng"). "Gói & Thanh toán" 16 ký tự bị cắt
    // cụt thành "Gói & Thanh…" — sếp Phú chụp lại 29.08. Nhãn thanh dọc để NGẮN; tên đầy đủ
    // nằm ở tiêu đề trang và breadcrumb.
    //
    // ⛔ KHÔNG đặt là "Thanh toán": CRM › Tài chính đã có mục tên đúng như vậy (/crm/payments —
    // tiền KHÁCH CỦA TENANT trả cho họ). Hai mục cùng tên ở hai cụm khác nhau là nguồn bấm nhầm.
    label: msg`Gói dịch vụ`,
    icon: CreditCard,
    basePath: '/billing',
    items: [
      { key: 'marketplace-modules', label: msg`Gói & Thanh toán`, path: '/billing', permission: 'marketplace:view' },
    ],
  },
];

// Chữ HIỆN của tiêu đề nhóm con («CÀI ĐẶT», «TÍCH HỢP», «HỆ THỐNG»…) — cnv-ui SidebarSecondary +
// PermissionsPage đọc `groupLabel` qua tLabel, thiếu thì in thẳng `group` (tiếng Việt, ở mọi ngôn ngữ).
// `group` vẫn là CHUỖI TRẦN vì nó là KHOÁ gom nhóm: SidebarSecondary so `item.group !== lastGroup`,
// moduleLock so ALWAYS_OPEN_SUBGROUPS, các cổng tools/ so nhãn với Core — bọc i18n vào `group` là
// gãy cả ba. Thêm nhóm con mới thì thêm một dòng ở đây.
const SUBGROUP_LABELS = {
  'Nhân sự': msg`Nhân sự`,
  'Hợp đồng': msg`Hợp đồng`,
  'Chấm công': msg`Chấm công`,
  'Nghỉ phép': msg`Nghỉ phép`,
  'Tài sản': msg`Tài sản`,
  'Tài sản số': msg`Tài sản số`,
  'Cài đặt': msg`Cài đặt`,
  'AI': msg`AI`,
  'CRM': 'CRM',
  'Khách hàng': msg`Khách hàng`,
  'Vận hành': msg`Vận hành`,
  'Tài chính': msg`Tài chính`,
  'Khuyến mãi': msg`Khuyến mãi`,
  'Tích hợp': msg`Tích hợp`,
  'Hệ thống': msg`Hệ thống`,
};
for (const g of NAV_GROUPS) {
  for (const it of g.items || []) {
    if (!it.group || it.groupLabel) continue;
    it.groupLabel = SUBGROUP_LABELS[it.group];
    if (import.meta.env.DEV && !it.groupLabel) {
      console.warn(`[AppLayout] nhóm con «${it.group}» (${it.key}) chưa khai SUBGROUP_LABELS — EN/FR sẽ hiện tiếng Việt`);
    }
  }
}

// Routes ẩn secondary submenu — chỉ root /inbox (Hộp thư) fullscreen như Core's Layout.
// Sub-pages (/inbox/canned-responses, /inbox/agents, ...) HIỂN THỊ submenu + padding bình thường.
// Core dùng ?id=<uuid> query param cho conversation detail, KHÔNG dùng path /inbox/:id.
// Detail patterns — match Core's AdminSidebar.DETAIL_PATTERNS (filter routes CNV-only).
// Khi match → secondary sidebar 240px ẨN, chỉ giữ primary rail 80px.
const DETAIL_PATTERNS = [
  /^\/inbox$/,
  /\/campaigns\/[^/]+$/,
  /\/campaigns\/[^/]+\/pipeline/,
  /\/recruitment\/c\//,
  /\/pipelines\/[^/]+$/,
  /\/pipelines\/new$/,
  /\/tasks\/projects\/[^/]+$/,
  /^\/crm\/reports\/.+$/,
  /^\/crm\/commissions$/,
  /^\/approvals\/templates\/(new|[a-zA-Z0-9-]+)$/,
  /^\/approvals\/[a-f0-9-]{8,}$/i,
  /^\/me\/?$/,                                       // personal settings — ẩn submenu
  /^\/billing\/?$/,                                  // Gói & Thanh toán — submenu 1 item, ẩn cho gọn
  /^\/marketplace\/?$/,                              // đường dẫn CŨ, còn sống để link đã gửi không chết
  // ⛔ Cụm Dự án + Xếp việc KHÔNG còn ẩn nav phụ (03/09/2026).
  //
  // Trước đó chúng nằm trong danh sách này vì 27/08 sếp Phú tắt nav phụ cho cụm Dự án —
  // "submenu đang khá vướng" — và bù lại bằng dãy icon chuyển màn ngay trên trang. Đến 03/09
  // dãy ấy bị bỏ hẳn ("bỏ luôn vì submenu đã có, xem lại để làm cho đúng chuẩn thiết kế hệ
  // thống đang có"), nên giữ dòng ẩn này là mất CẢ HAI đường: không dãy, không menu con —
  // vào /projects rồi không cách nào sang Bảng giai đoạn hay Bản đồ ngoài gõ tay URL.
  //
  // Nay theo đúng chuẩn cụm Nhân sự: menu con luôn mở ở thanh trái lo điều hướng.
];

// Fullscreen: ẩn cả 2 sidebar tier (chỉ primary 80px), content fixed inset.
const FULLSCREEN_PATTERNS = [
  /^\/inbox$/,
  /^\/approvals\/templates\/(new|[a-zA-Z0-9-]+)$/,   // template builder
  // Trang soạn biểu mẫu dự án mượn ĐÚNG khuôn của template builder ngay trên (sếp Phú
  // 03/09: *"tham khảo mẫu phê duyệt mà làm cho đúng"*). Khuôn đó chỉ thành hình khi vỏ
  // app cho trang chiếm trọn màn: thiếu dòng này thì `FormTemplateEditor asPage` nằm
  // trong vỏ `px-8 pt-8`, nền slate-50 co thành một mảng lọt giữa chứ không ra nền
  // trang — đúng hiện tượng "trang này lại ko có background gì".
  /^\/projects\/form-templates\/(new|[a-zA-Z0-9-]+)$/,
  /^\/approvals\/[a-f0-9-]{8,}$/i,                    // approval detail
  /^\/me\/?$/,                                        // personal settings
  /^\/automation\/?$/,
  /^\/automation\/[a-zA-Z0-9-]+$/,
  /^\/chat(\/.*)?$/,                                  // team chat — Slack-style full page
  /^\/tickets\/[a-f0-9-]+$/i,                         // ticket detail — match Core Layout.jsx FULLSCREEN
  /^\/support\/[a-f0-9-]+$/i,                         // legacy /support/:id (trước khi RedirectSupportToTicket fire)
];

// Fullbleed: content sát top + full width (no padding), nhưng VẪN giữ 2-tier sidebar.
// Page tự handle header sticky. Khớp Core's isApprovalsFullBleed.
// Core FULLBLEED chỉ /tasks(/.*) + /approvals/... — KHÔNG có /crm.
// /crm KHÔNG fullbleed: dùng padding chuẩn (px-8 pt-8), tránh body tràn đụng secondary sidebar (240px).
const FULLBLEED_PATTERNS = [
  /^\/approvals\/?$/,                                 // inbox + sub-tabs
  /^\/approvals\/(inbox|templates|report|shared)\/?$/,
  /^\/tasks\/?$/,
  /^\/tasks\/board$/,
];

// Hết 14 ngày dùng thử mà chưa từng mua (sếp Phú 30/10/2026 — «off tài khoản nhưng chưa xoá»): máy chủ chặn MỌI request
// trừ /marketplace + /auth (moduleAccessService.trial_off) ⇒ dựng màn mua gói TOÀN TRANG thay cả khung app. Tách ở cửa
// này, TRƯỚC mọi hook của khung (badge poll, realtime, prefetch route): khung không mount thì không bắn loạt 402 vô ích.
export default function AppLayout() {
  const trialOff = useAuthStore((s) => s.user?.module_access?.trial_off === true);
  return trialOff ? <TrialExpiredHost /> : <AppLayoutMain />;
}

function TrialExpiredHost() {
  const user = useAuthStore((s) => s.user);
  const activeTenant = useAuthStore((s) => s.activeTenant);
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <TrialExpiredScreen
      access={user?.module_access}
      workspaceName={activeTenant?.display_name}
      onLogout={logout}
      onSwitchWorkspace={() => navigate('/select-workspace')}
    />
  );
}

function AppLayoutMain() {
  const activeTenant = useAuthStore((s) => s.activeTenant);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  // Private module gate: filter menu groups có `privateModule` — chỉ hiện khi
  // tenant.private_modules[name] = true. Match BE middleware requirePrivateModule.
  const privateModules = activeTenant?.private_modules || {};
  // Permission gate (2026-05-27): filter từng item theo `item.permission`.
  // Item KHÔNG có `permission` → ai cũng thấy (default).
  // Group hết items sau filter → bỏ luôn group (tránh hiển thị icon group rỗng).
  const visibleMenu = NAV_GROUPS
    .filter((g) => !g.hidden)
    .filter((g) => !g.privateModule || privateModules[g.privateModule] === true)
    .map((g) => {
      // Group single-link (`path`, không `items`): gate nằm ở CẤP NHÓM. Group nào không khai
      // `permission` (vd Automation) thì giữ nguyên như trước — ai cũng thấy.
      if (!Array.isArray(g.items)) return (g.permission && !userHasPermission(user, g.permission)) ? null : g;
      // Item gate: permission + (optional) privateModule per-item (vd Bot gate
      // theo team_chat — mirror Core adminMenu.js).
      const items = g.items.filter((it) =>
        userHasPermission(user, it.permission)
        && (!it.privateModule || privateModules[it.privateModule] === true));
      return { ...g, items };
    })
    // Giữ single-link group (có `path`, vd Automation/Reports) dù không có items; chỉ loại
    // group HAD items nhưng bị permission-filter hết → tránh icon group rỗng.
    // `g && ...`: nhánh single-link phía trên trả null khi thiếu quyền — thiếu chốt này là
    // đọc `.path` của null ⇒ TRẮNG TRANG cả app với user không có quyền (bản thân admin
    // thì không bao giờ gặp).
    .filter((g) => g && (g.path || !Array.isArray(g.items) || g.items.length > 0));

  // Badge counters — gộp polling 1 endpoint /badges/summary + realtime team_chat unread.
  // poll 180s = LƯỚI AN TOÀN (time-based overdue + event lỡ); realtime (useBadgeRealtime) lo
  // refresh TỨC THÌ khi server emit badge:refresh (task/approval/ticket... assign cho user).
  // Prefetch nền mọi chunk route sau khi vào app → bấm menu hết spinner "loading".
  useEffect(() => { prefetchAllRoutes(); }, []);

  useBadgeRealtime();
  const polledBadges = useSidebarBadges(SIDEBAR_BADGE_ENDPOINTS, { api, pollIntervalMs: 180_000 });
  const teamChatUnread = useTeamChatUnreadTotal();
  const badgeCounts = { ...polledBadges, team_chat: teamChatUnread };

  // ── Giao diện v2 ────────────────────────────────────────────────────────────
  // `data-v2-shell` trên <body> là cổng của MỌI rule vỏ trong admin/src/styles/ui-v2.css.
  // Đặt ở đây (chứ không phải trên <html>) để trang NGOÀI layout — login, nhận lời mời,
  // chọn workspace, link chia sẻ công khai — không bị lớp phủ vỏ chạm tới dù cờ v2 đang bật.
  // Gate tenant là CHỐT CHẶN CỨNG, đứng trước lựa chọn của user: tenant ngoài allowlist
  // không bao giờ thấy v2, kể cả khi localStorage còn sót ui_version='v2'.
  const tenantAllowed = isTenantAllowed(activeTenant?.slug);
  const [uiChoice, setUiChoice] = useState(() => readUiVersion());
  useEffect(() => subscribeUiVersion(setUiChoice), []);
  const isV2 = tenantAllowed && uiChoice === UI_V2;
  // activeTenant về SAU lần render đầu (từ /auth/me) → phải áp lại attribute khi biết tenant,
  // nếu không thì tenant không được phép vẫn giữ data-ui mà IIFE boot đã set từ slug cũ
  // (vd user vừa đổi workspace từ beta sang tenant khác trong cùng tab).
  useEffect(() => { applyUiVersion(isV2 ? UI_V2 : UI_V1); }, [isV2]);
  useEffect(() => {
    document.body.setAttribute('data-v2-shell', '');
    return () => document.body.removeAttribute('data-v2-shell');
  }, []);
  // Màn hình cần BỀ NGANG: ẩn menu con để lấy thêm 240px. Hiện chỉ có kanban của Deals —
  // 290px/cột nên còn submenu thì chỉ vừa 4 cột, kéo ngang suốt (sếp Phú chỉ ra 14/08).
  // Không dùng detailPatterns của cnv-ui được: nó chỉ khớp theo pathname, mà đây là
  // query `?view=kanban`. Vì vậy đánh dấu bằng attribute rồi để ui-v2.css lo phần ẩn.
  const wideView = new URLSearchParams(location.search).get('view') === 'kanban';
  useEffect(() => {
    if (wideView) document.body.setAttribute('data-v2-wide', '');
    else document.body.removeAttribute('data-v2-wide');
    return () => document.body.removeAttribute('data-v2-wide');
  }, [wideView]);

  // Route guard quyền (2026-07-15) — derive từ chính NAV_GROUPS nên không lệch menu.
  // Dùng NAV_GROUPS gốc (KHÔNG phải visibleMenu đã lọc): visibleMenu bỏ item user không có quyền
  // → path đó biến mất khỏi map → checkRoutePermission fail-closed sẽ chặn NHẦM vì "không khớp"
  // thay vì vì "thiếu quyền". Cùng kết quả ở đây, nhưng sai lý do và sẽ vỡ khi nới rule sau này.
  const routeMap = useMemo(() => buildRouteMap(NAV_GROUPS), []);
  const isAdmin = user?.role === 'admin' || (user?.permissions || []).includes('*');
  const allowed = checkRoutePermission(
    location.pathname, routeMap, (p) => userHasPermission(user, p), isAdmin,
  );

  // Khoá tính năng chưa mua (sếp Phú 15/09/2026): trang thuộc module workspace chưa mua ⇒ thay nội dung bằng
  // màn bảng giá, GIỮ menu (ẩn menu là giấu thứ muốn bán — cùng lý do dòng chú thích đầu file). Quyền xem
  // vẫn xét trước: người không có quyền vào trang thì vẫn bị đá về /dashboard như cũ.
  const moduleRouteMap = useMemo(() => buildModuleRouteMap(NAV_GROUPS), []);
  const lockedModule = lockedModuleForPath(location.pathname, moduleRouteMap, user?.module_access);

  // ⚠️ Đặt SAU toàn bộ hook — early-return trước hook là vi phạm Rules of Hooks (lint:hooks chặn).
  // BE mới là chốt chặn thật (requirePermission); đây chỉ tránh user gõ URL vào trang rồi ăn 403.
  if (!allowed) return <Navigate to="/dashboard" replace />;

  const brand = {
    // Custom tenant logo nếu admin upload (lưu meta.tenants.metadata.branding.logo_url).
    // Fallback default CNV Work logo.
    logo: activeTenant?.branding?.logo_url || '/cnvwork.png',
    appName: activeTenant?.branding?.app_name || 'CNV Work',
    appShort: activeTenant?.branding?.app_short || 'CNV',
    subtitle: activeTenant?.display_name,
  };

  return (
    <>
      {/* badgeCounts bỏ 15/08: thanh chỉ còn breadcrumb + gói + avatar, không còn badge nào
          đọc nó (nút Xuất file đã gỡ theo yêu cầu sếp Phú). */}
      {isV2 && <TopBarV2 menu={visibleMenu} brand={brand} />}
      {/* v1 không có thanh trên cùng → pill nổi là lối duy nhất để bật lại v2.
          Chỉ hiện cho tenant trong allowlist: tenant khác không được thấy dấu vết nào của v2. */}
      {!isV2 && tenantAllowed && <UiVersionSwitch floating />}
      <AppLayoutShared
        menu={visibleMenu}
        brand={brand}
        detailPatterns={DETAIL_PATTERNS}
        fullscreenPatterns={FULLSCREEN_PATTERNS}
        fullbleedPatterns={FULLBLEED_PATTERNS}
        customSecondary={CUSTOM_SECONDARY}
        badgeCounts={badgeCounts}
        showChatDock
        {...(isV2 ? { primaryBottomSlot: <RailBottomV2 /> } : null)}
      >
        {/* null ⇒ AppLayoutShared tự rơi về <Outlet /> (children ?? <Outlet />) */}
        {lockedModule
          ? <ModuleLockScreen moduleCode={lockedModule} access={user.module_access} />
          : null}
      </AppLayoutShared>
      {/* Khung đọc bài «Tin mới» — mở từ chuông, thẻ bot «Thông báo» trong chat, link /whats-new/<id>
          hay ?newsId=. Mount cả ở v1 (không có thanh trên cùng) để thẻ chat vẫn mở được bài. */}
      <ProductNewsHost />
    </>
  );
}
